import React, { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import Select from 'react-select'

import EmptyRow from '@components/EmptyRow'
import Spinner from '@components/Spinner'
import Table from '@components/Table'
import Td from '@components/Td'
import Tr from '@components/Tr'
import { errorMessages, successMessages } from '@constants/feedbackMessages'
import { LawArticle } from '@models/lawArticle.model'
import { disciplineApi } from '@services/DisciplineApi'
import { filtersApi } from '@services/filtersApi'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch } from '@store/hooks'

const styles = {
	table: 'mt-6',
	intro: 'mt-6 text-sm text-gray',
	article: 'font-medium text-gray-900',
	theme: 'w-[60%] px-6',
}

export default function LawArticlesTable() {
	const { id, subSubjectId } = useParams<{ id: string; subSubjectId: string }>()
	const dispatch = useAppDispatch()

	const { data, isLoading } = disciplineApi.useListLawArticlesQuery({
		subMatterId: subSubjectId,
	})
	const [updateLawArticle, { isLoading: isSaving }] =
		disciplineApi.useUpdateLawArticleMutation()

	const { data: matters } = filtersApi.useListMatterQuery({
		disciplineId: id || '-1',
	})
	const matterIds = (matters?.data ?? []).map((matter) => matter.id).join(',')
	const { data: subMatters } = filtersApi.useListSubMatterQuery(
		{ matterId: matterIds },
		{ skip: !matterIds }
	)

	const themeOptions = useMemo(
		() =>
			(subMatters?.data ?? []).map((subMatter) => ({
				value: subMatter.id,
				label: subMatter.name,
				matterId: Number(subMatter.matterId),
			})),
		[subMatters]
	)

	async function moveTo(article: LawArticle, subMatterId: number) {
		try {
			await updateLawArticle({ id: article.id, subMatterId }).unwrap()
			dispatch(notificationCreators.show(successMessages.movedLawArticle))
		} catch {
			dispatch(notificationCreators.show(errorMessages.moveLawArticle))
		}
	}

	if (isLoading) {
		return (
			<div className='flex justify-center my-10'>
				<Spinner size='md' />
			</div>
		)
	}

	const articles = data?.data ?? []

	return (
		<>
			<p className={styles.intro}>
				Artigos lidos das questões cujo tema é este sub-assunto. Eles aparecem
				debaixo dele no filtro do site e contam no card dele no raio-x.
			</p>
			<div className={styles.table}>
				<Table
					th={[{ label: 'Artigo' }, { label: 'Questões' }, { label: 'Tema' }]}
				>
					{articles.length === 0 ? (
						<EmptyRow message='Nenhum artigo neste sub-assunto' tdAmount={3} />
					) : (
						articles.map((article) => (
							<Tr key={article.id}>
								<Td>
									<span className={styles.article}>Art. {article.label}</span>
								</Td>
								<Td>{article.questionCount}</Td>
								<Td className={styles.theme}>
									<Select
										inputId={`law-article-theme-${article.id}`}
										isDisabled={isSaving}
										options={themeOptions.filter(
											(option) =>
												!article.scopeMatterId ||
												option.matterId === article.scopeMatterId
										)}
										value={themeOptions.find(
											(option) => option.value === article.subMatterId
										)}
										onChange={(option) => {
											if (option && option.value !== article.subMatterId) {
												moveTo(article, option.value)
											}
										}}
										classNamePrefix='react-select'
										menuPortalTarget={document.body}
									/>
								</Td>
							</Tr>
						))
					)}
				</Table>
			</div>
		</>
	)
}
